import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils';

const pngIconModules = import.meta.glob<string>('../../assets/icons/*.png', {
  import: 'default',
  eager: true,
});

const pngIconSrc: Record<string, string> = {};
for (const [path, src] of Object.entries(pngIconModules)) {
  const match = path.match(/\/([^/]+)\.png$/);
  if (match?.[1]) {
    pngIconSrc[match[1]] = src;
  }
}

const svgIconModules = import.meta.glob<string>('../../assets/icons/*.svg', {
  query: '?raw',
  import: 'default',
  eager: true,
});

// Keep in sync with ../../assets/icons/*.svg — validated by icon.spec.tsx
type IconName =
  | 'account-header'
  | 'activation'
  | 'add-option'
  | 'arrow-left'
  | 'arrow-right'
  | 'bell'
  | 'bell-active'
  | 'calendar'
  | 'checkbox-check'
  | 'check'
  | 'check-big'
  | 'check-circle'
  | 'check-circle-filled'
  | 'chevron-down'
  | 'chevron-left'
  | 'chevron-right'
  | 'chevron-up'
  | 'circle'
  | 'circle-alert'
  | 'circle-help'
  | 'close'
  | 'copy'
  | 'credentials'
  | 'devices'
  | 'download'
  | 'error'
  | 'external-link'
  | 'eye-closed'
  | 'eye-open'
  | 'face-frown'
  | 'hardware'
  | 'home'
  | 'info'
  | 'invoices'
  | 'laptop'
  | 'line'
  | 'loader'
  | 'logo-waipu-icon'
  | 'logo-waipu-wordmark'
  | 'logout'
  | 'mail'
  | 'minimal'
  | 'minus'
  | 'more-horizontal'
  | 'network-error'
  | 'package'
  | 'parental-control'
  | 'payment'
  | 'personal-data'
  | 'phone'
  | 'pin'
  | 'placeholder'
  | 'play-circle'
  | 'privacy'
  | 'progress'
  | 'returns'
  | 'search'
  | 'shield'
  | 'shield-generic'
  | 'sparkles'
  | 'subscription'
  | 'switch-package'
  | 'tablet'
  | 'triangle-alert'
  | 'truck'
  | 'tv'
  | 'tv-play'
  | 'voucher'
  | 'website'
  | 'withdrawal';

const svgIconContent: Record<string, string> = {};
for (const [path, content] of Object.entries(svgIconModules)) {
  const match = path.match(/\/([^/]+)\.svg$/);
  if (match?.[1]) {
    svgIconContent[match[1]] = content;
  }
}

const iconNames = new Set<string>([...Object.keys(pngIconSrc), ...Object.keys(svgIconContent)]);

// Sizes map to design tokens in theme.css. `lg` (24px) is the default — the most common size in usage.
const iconVariants = cva('inline-flex shrink-0 items-center justify-center', {
  variants: {
    size: {
      xs: 'size-icon-xs', // 12px
      sm: 'size-icon-sm', // 16px
      md: 'size-icon-md', // 20px
      lg: 'size-icon-lg', // 24px
      xl: 'size-icon-xl', // 32px
      '2xl': 'size-icon-2xl', // 48px
      '3xl': 'size-icon-3xl', // 64px
    },
  },
  defaultVariants: {
    size: 'lg',
  },
});

type IconSize = NonNullable<VariantProps<typeof iconVariants>['size']>;

interface IconBaseProps {
  name: IconName;
  size?: IconSize;
  /** Layout-only className (margins, alignment). Sizing comes from `size`; color inherits from the parent via `currentColor`. */
  className?: string;
}

// Discriminated by a11y intent: either the icon is decorative (default) or it conveys meaning and needs a label.
type IconProps = IconBaseProps & ({ 'aria-label'?: never } | { 'aria-label': string });

function Icon({ name, size, className, 'aria-label': ariaLabel }: IconProps) {
  const isPng = name in pngIconSrc;
  const a11yProps = ariaLabel ? ({ role: 'img' as const, 'aria-label': ariaLabel } as const) : ({ 'aria-hidden': true as const } as const);

  if (isPng) {
    const src = pngIconSrc[name];
    if (!src) {
      return null;
    }
    return (
      <span className={cn(iconVariants({ size }), className)} {...a11yProps}>
        <img src={src} alt="" loading="eager" className="block size-full object-contain" />
      </span>
    );
  }

  const svgContent = svgIconContent[name];
  if (!svgContent) {
    return null;
  }

  return (
    <span
      className={cn(iconVariants({ size }), '[&_svg]:block [&_svg]:size-full', className)}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: SVG content from local build-time assets, not user input
      dangerouslySetInnerHTML={{ __html: svgContent }}
      {...a11yProps}
    />
  );
}

export type { IconName, IconProps, IconSize };
export { Icon, iconNames };
